import React from "react";
import { Button } from "@/components/ui/button";
import { Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CartItem } from "../store/cart-store";
import { useCart } from "../hooks/use-cart";
import { CartItemCardMobile } from "./CartItemCardMobile";
import { CartItemCardDesktop } from "./CartItemCardDesktop";
import { useProductDetailModal } from "@/features/product-detail/hooks/use-product-detail-modal";

export interface CartItemCardProps {
  item: CartItem;
  className?: string;
  compact?: boolean; // Mini cart için compact view
  onClose?: () => void; // Dropdown'ı kapatmak için
  // 🎨 Yeni Customization Props
  variant?: "default" | "cart-page" | "dropdown"; // Görünüm variant'ı
  showBorder?: boolean; // Border göster/gizle
  showShadow?: boolean; // Shadow göster/gizle
  imageSize?: "sm" | "md" | "lg" | "xl"; // Görsel boyutu
  layout?: "horizontal" | "vertical"; // Layout tipi
}

export const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  className,
  compact = false,
  onClose,
  variant = "default",
  showBorder = true,
  showShadow = true,
  imageSize = "md",
}) => {
  const {
    updateQuantity,
    removeFromCart,
    formatPrice: formatPriceHook,
  } = useCart();

  // Safe formatPrice with NaN handling
  const formatPrice = (price: number | undefined | null) => {
    if (typeof price !== "number" || isNaN(price)) {
      return formatPriceHook(0);
    }
    return formatPriceHook(price);
  };
  const { openModal } = useProductDetailModal();

  const handleQuantityIncrease = (e: React.MouseEvent) => {
    e.stopPropagation(); // Parent onClick'i tetikleme
    updateQuantity(item._id, item.quantity + 1);
  };

  const handleQuantityDecrease = (e: React.MouseEvent) => {
    e.stopPropagation(); // Parent onClick'i tetikleme
    if (item.quantity > 1) {
      updateQuantity(item._id, item.quantity - 1);
    } else {
      removeFromCart(item._id);
    }
  };

  const handleProductClick = () => {
    const productIdForDetail = item._product || item.productId;
    if (productIdForDetail) {
      openModal(productIdForDetail);
    }

    // Dropdown varsa kapat
    if (onClose) onClose();
  };

  // Item total price - Backend'den gelen price kullan (NaN kontrolü ile)
  const itemPrice =
    typeof item.price === "number" && !isNaN(item.price) ? item.price : 0;
  const itemTotal = itemPrice * item.quantity;

  // Yeni data'dan variant bilgileri (metadata'dan al - backward compatibility)
  const variant1 =
    item.metadata?.axis1Name && item.metadata?.a1Value
      ? { name: item.metadata.axis1Name, value: item.metadata.a1Value }
      : null;
  const variant2 =
    item.metadata?.axis2Name && item.metadata?.a2Value
      ? { name: item.metadata.axis2Name, value: item.metadata.a2Value }
      : null;

  // Campaign price bilgisi (metadata'dan)
  const originalPrice = item.metadata?.originalPrice;
  const hasDiscount =
    originalPrice &&
    typeof originalPrice === "number" &&
    originalPrice > itemPrice;
  const discountPct =
    hasDiscount && typeof originalPrice === "number"
      ? Math.round(((originalPrice - itemPrice) / originalPrice) * 100)
      : 0;

  // Stock bilgisi (metadata'dan)
  const stockCount = item.metadata?.stockCount as number | undefined;
  const inStock = item.metadata?.inStock !== false;

  // Image size mapping
  const imageSizeClasses = {
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-28 h-28", // 112px - Figma'ya daha uygun
    xl: "w-32 h-32", // 128px
  };

  // 🛒 Cart Page Variant - Figma Design (Horizontal Compact)
  if (variant === "cart-page") {
    return (
      <div
        className={cn(
          "flex gap-2 md:gap-4 py-2 md:py-3",
          !showBorder && "border-0",
          !showShadow && "shadow-none",
          className
        )}
      >
        {/* Product Image */}
        <div
          className={cn(
            "rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 cursor-pointer",
            imageSize === "xl"
              ? "w-20 h-20 md:w-32 md:h-32"
              : imageSizeClasses[imageSize]
          )}
          onClick={handleProductClick}
        >
          {item.file ? (
            <img
              src={item.file}
              alt={item.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <ShoppingBag className="h-6 w-6" />
            </div>
          )}
        </div>

        {/* Product Details - Horizontal Layout */}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          {/* Top: Title + Price */}
          <div className="flex justify-between items-start gap-2 md:gap-4">
            {/* Left: Title + Brand + Variants */}
            <div className="flex-1 min-w-0">
              <h3
                className="font-medium text-sm md:text-base text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-2 leading-tight"
                onClick={handleProductClick}
              >
                {item.title}
              </h3>

              {/* Brand - Clickable */}
              {item.metadata?.brand && (
                <button
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors mt-1 underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    // TODO: Navigate to brand page
                    console.log("Brand clicked:", item.metadata?.brand);
                  }}
                >
                  {item.metadata.brand}
                </button>
              )}

              {/* Variant Info */}
              {(variant1 || variant2) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {[variant1?.value, variant2?.value]
                    .filter(Boolean)
                    .join(" / ")}
                </p>
              )}
            </div>

            {/* Right: Price */}
            <div className="flex flex-col items-end">
              {hasDiscount && originalPrice ? (
                <>
                  <span className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatPrice(itemTotal)}
                  </span>
                  <span className="text-[10px] md:text-xs line-through text-gray-400 dark:text-gray-500">
                    {formatPrice((originalPrice as number) * item.quantity)}
                  </span>
                </>
              ) : (
                <span className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {formatPrice(itemTotal)}
                </span>
              )}

              {/* Unit Price */}
              {item.quantity > 1 && (
                <span className="text-[10px] md:text-xs text-gray-400 dark:text-gray-500">
                  {formatPrice(itemPrice)} × {item.quantity}
                </span>
              )}
            </div>
          </div>

          {/* Bottom: Quantity Controls */}
          <div className="flex items-center gap-2 md:gap-3 mt-1 md:mt-2">
            {/* Trash Icon */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeFromCart(item._id);
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>

            {/* Quantity Controls */}
            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
              <button
                onClick={handleQuantityDecrease}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[20px] text-center">
                {item.quantity}
              </span>
              <button
                onClick={handleQuantityIncrease}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    // 📱 Compact view for mini cart dropdown
    return (
      <div
        className={cn(
          "flex gap-3 p-3 bg-background rounded-lg border border-tertiary",
          "hover:shadow-md transition-colors duration-200 dark:hover:shadow-indigo-500/10",
          className
        )}
      >
        {/* Left Side: Image + Quantity Controls */}
        <div className="flex flex-col items-center justify-between">
          {/* Product Image - Larger */}
          <div
            className="w-20 h-20 rounded-md overflow-hidden bg-muted flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all duration-200"
            onClick={handleProductClick}
          >
            {item.file ? (
              <img
                src={item.file}
                alt={item.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <ShoppingBag className="h-6 w-6" />
              </div>
            )}
          </div>

          {/* Quantity Controls - Bottom Aligned */}
          <div className="flex items-center gap-1 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleQuantityDecrease}
              className="h-6 w-6 p-0"
            >
              <Minus className="h-3 w-3" />
            </Button>
            <span className="text-xs w-6 text-center font-medium">
              {item.quantity}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleQuantityIncrease}
              className="h-6 w-6 p-0"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Right Side: Product Details */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          {/* Top Section */}
          <div>
            <h4
              className="font-medium text-sm line-clamp-2 cursor-pointer transition-colors duration-200 hover:text-primary mb-1"
              onClick={handleProductClick}
            >
              {item.title}
            </h4>

            {/* Variant Badges */}
            {(variant1 || variant2) && (
              <div className="flex flex-wrap items-center gap-1 mb-1">
                {variant1 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                    {variant1.value}
                  </span>
                )}
                {variant2 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                    {variant2.value}
                  </span>
                )}
              </div>
            )}

            {/* Stock Warning */}
            {inStock && stockCount && stockCount < 10 && (
              <div className="mb-1">
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                  Son {stockCount} ürün!
                </span>
              </div>
            )}

            {/* Seller Name */}
            {item.metadata?.sellerName && (
              <p className="text-[10px] text-muted-foreground">
                {item.metadata.sellerName}
              </p>
            )}
          </div>

          {/* Bottom Section: Price */}
          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            {hasDiscount && originalPrice ? (
              <>
                <span className="text-xs line-through text-muted-foreground">
                  {formatPrice(originalPrice as number)}
                </span>
                <span className="text-sm font-semibold text-text-primary">
                  {formatPrice(itemTotal)}
                </span>
                <span className="px-1 py-0.5 bg-red-500 text-white rounded text-[10px] font-semibold">
                  %{discountPct}
                </span>
              </>
            ) : (
              <span className="text-sm font-semibold text-text-primary">
                {formatPrice(itemTotal)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 🖥️ Full view for cart page - Responsive Components
  return (
    <>
      {/* Mobile Component */}
      <div className="block md:hidden">
        <CartItemCardMobile item={item} className={className} />
      </div>

      {/* Desktop Component */}
      <div className="hidden md:block">
        <CartItemCardDesktop item={item} className={className} />
      </div>
    </>
  );
};
